import { Calendar, Clock, Info } from 'lucide-react';
import { useId, useState } from 'react';

/**
 * Props för DateTimePicker-komponenten
 */
export interface DateTimePickerProps {
  /** Valt datum/tid */
  value: Date;
  /** Callback när datum/tid ändras */
  onChange: (date: Date) => void;
  /** Label för inputfältet */
  label?: string;
  /** Minimalt tillåtet datum (default: nu) */
  minDate?: Date;
  /** Maximalt tillåtet datum (default: nu + 10 dagar) */
  maxDate?: Date;
}

/**
 * Konverterar Date-objekt till datetime-local format (YYYY-MM-DDTHH:mm)
 */
function formatDateTimeLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Rundar tid till närmsta 15-minutersintervall
 */
export function roundToNearest15Minutes(date: Date): Date {
  const rounded = new Date(date);
  const minutes = rounded.getMinutes();
  const roundedMinutes = Math.round(minutes / 15) * 15;

  rounded.setMinutes(roundedMinutes);
  rounded.setSeconds(0);
  rounded.setMilliseconds(0);

  return rounded;
}

/**
 * Beräknar default-värde: nu + 1 timme, avrundat till närmsta 15 min
 */
export function getDefaultDateTime(): Date {
  const now = new Date();
  const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
  return roundToNearest15Minutes(oneHourLater);
}

/**
 * DateTimePicker - Snygg datumväljare för resplanering
 *
 * Features:
 * - Native datetime-local input för bästa kompatibilitet
 * - Default: nu + 1h, avrundat till närmsta 15 min
 * - Min: nu, Max: nu + 10 dagar (SMHI:s begränsning)
 * - Snygg design med Lucide-ikoner
 * - Svensk formatering
 */
export default function DateTimePicker({
  value,
  onChange,
  label = 'När vill du åka?',
  minDate,
  maxDate,
}: DateTimePickerProps) {
  const inputId = useId();
  const [showTooltip, setShowTooltip] = useState(false);

  // Default min/max-värden
  const now = new Date();
  const tenDaysFromNow = new Date(now.getTime() + 10 * 24 * 60 * 60 * 1000);

  const effectiveMinDate = minDate || now;
  const effectiveMaxDate = maxDate || tenDaysFromNow;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(e.target.value);

    // Validera att datumet är giltigt
    if (!isNaN(newDate.getTime())) {
      onChange(newDate);
    }
  };

  return (
    <div className="space-y-1">
      {/* Label med info-ikon */}
      <div className="flex items-center gap-2">
        <label htmlFor={inputId} className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Calendar className="w-4 h-4 text-blue-600" />
          {label}
        </label>
        {/* Info-ikon med tooltip */}
        <div className="relative">
          <button
            type="button"
            className="p-0.5 rounded-full hover:bg-gray-100 transition-colors"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onFocus={() => setShowTooltip(true)}
            onBlur={() => setShowTooltip(false)}
            aria-label="Information om väderprognos"
          >
            <Info className="w-4 h-4 text-gray-400 hover:text-blue-500" />
          </button>
          {showTooltip && (
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-50">
              SMHI ger väderprognos upp till 10 dagar framåt
              <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800" />
            </div>
          )}
        </div>
      </div>

      {/* Input wrapper med gradient och ikoner */}
      <div className="relative w-full">
        {/* Vänster ikon - Klocka */}
        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10">
          <Clock className="w-5 h-5 text-blue-500" />
        </div>

        {/* Datetime-local input */}
        <input
          id={inputId}
          type="datetime-local"
          value={formatDateTimeLocal(value)}
          onChange={handleChange}
          min={formatDateTimeLocal(effectiveMinDate)}
          max={formatDateTimeLocal(effectiveMaxDate)}
          step="900" // 15 minuter i sekunder
          className="input pl-12 pr-4 cursor-pointer
                     bg-gradient-to-r from-blue-50/50 to-cyan-50/50
                     hover:from-blue-50 hover:to-cyan-50
                     transition-colors"
          aria-label={label}
        />

        {/* Gradient-overlay för snyggare look */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/5 to-cyan-500/5 pointer-events-none" />
      </div>
    </div>
  );
}
